import React, { useState } from 'react';
import { useAuth } from '../App';
import { 
  Check, 
  Crown, 
  Zap, 
  Building2, 
  Rocket,
  Settings,
  Star,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const tiers = [
  {
    id: 'tier1',
    name: 'EBA Payroll Assist',
    subtitle: 'MVP Tier',
    description: 'Perfect for small-mid crane companies with 1-2 EBAs doing manual payroll today.',
    monthlyPrice: 450,
    monthlyRange: '$300 – $600',
    yearlyPrice: 4750,
    yearlyRange: '$3,500 – $6,000',
    icon: Zap,
    color: 'from-blue-500 to-blue-600',
    borderColor: 'border-blue-200',
    bgColor: 'bg-blue-50',
    features: [
      { text: 'Timesheet import from EQUIPR', included: true },
      { text: 'Automated EBA interpretation', included: true },
      { text: 'Ordinary / OT / Allowances breakdown', included: true },
      { text: 'Excel + payroll CSV export', included: true },
      { text: 'Explainable rule audit', included: true },
      { text: '1-2 EBA documents', included: true },
      { text: 'Direct Xero export', included: false },
      { text: 'Multiple EBA versioning', included: false },
      { text: 'Admin EBA rule editor', included: false },
      { text: 'Priority support', included: false },
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'tier2',
    name: 'EBA Payroll + Accounting Sync',
    subtitle: 'Growth Tier',
    description: 'For growing crane businesses with multiple EBAs and payroll risk exposure.',
    monthlyPrice: 1000,
    monthlyRange: '$800 – $1,200',
    yearlyPrice: 11000,
    yearlyRange: '$9,000 – $13,000',
    icon: Rocket,
    color: 'from-equipr-black to-slate-800',
    borderColor: 'border-equipr-yellow',
    bgColor: 'bg-equipr-yellow/10',
    features: [
      { text: 'Everything in Tier 1', included: true, highlight: true },
      { text: 'Direct Xero export', included: true },
      { text: 'Payroll-ready file validation', included: true },
      { text: 'Reduced double handling', included: true },
      { text: 'Fewer payroll errors', included: true },
      { text: 'Up to 5 EBA documents', included: true },
      { text: 'Multiple EBA versioning', included: false },
      { text: 'Admin EBA rule editor', included: false },
      { text: 'Priority support', included: false },
    ],
    cta: 'Upgrade to Growth',
    popular: true,
  },
  {
    id: 'tier3',
    name: 'EBA Enterprise',
    subtitle: 'The Moat',
    description: 'For large fleets with Tower + Mobile + Civil EBAs and high compliance exposure.',
    monthlyPrice: 2400,
    monthlyRange: '$1,800 – $3,000+',
    yearlyPrice: null,
    yearlyRange: 'Custom pricing',
    icon: Crown,
    color: 'from-amber-500 to-amber-600',
    borderColor: 'border-amber-200',
    bgColor: 'bg-amber-50',
    features: [
      { text: 'Everything in Tier 2', included: true, highlight: true },
      { text: 'Multiple EBAs with versioning', included: true },
      { text: 'Effective-date handling', included: true },
      { text: 'Admin EBA rule editor', included: true },
      { text: 'Complete audit trail', included: true },
      { text: 'Unlimited EBA documents', included: true },
      { text: 'Priority support', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Custom integrations', included: true },
    ],
    cta: 'Contact Sales',
    popular: false,
    enterprise: true,
  },
];

const addOns = [
  {
    name: 'EBA Setup & Onboarding',
    description: 'One-off professional service to interpret your EBA, validate rules, and run parallel payroll testing.',
    price: '$2,500 – $10,000',
    type: 'one-time',
    features: ['EBA interpretation', 'Rule validation', 'Parallel run against manual payroll', 'De-risk adoption'],
  },
  {
    name: 'Additional EBA Packs',
    description: 'For companies with multiple EBAs beyond their tier limit.',
    price: '$200 – $400',
    type: 'per-month',
    features: ['Per additional EBA', 'Full interpretation', 'Rule management', 'Ongoing updates'],
  },
];

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  // Determine current tier based on client - computed value, no need for state
  const currentTier = user?.client_id === 'EQUIPR-001' ? 'tier3' : 'tier1';

  const getCurrentTierInfo = () => {
    return tiers.find(t => t.id === currentTier);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-equipr-yellow">
            <Crown className="w-5 h-5 text-equipr-black" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Subscription & Pricing</h1>
        </div>
        <p className="text-slate-600">
          Choose the plan that best fits your business needs. All plans include core EBA interpretation features.
        </p>
      </div>

      {/* Current Plan Banner */}
      <Card className="mb-8 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-slate-900">Current Plan: {getCurrentTierInfo()?.name}</h3>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">Enterprise</Badge>
                </div>
                <p className="text-sm text-slate-600">
                  Client: {user?.client_id} • Full access to all features
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{getCurrentTierInfo()?.monthlyRange}</p>
              <p className="text-sm text-slate-500">per month</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-8">
        <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="monthly" className="px-6">Monthly</TabsTrigger>
            <TabsTrigger value="yearly" className="px-6">
              Yearly
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700 text-xs">Save 15%</Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {tiers.map((tier) => {
          const isCurrentPlan = tier.id === currentTier;
          const Icon = tier.icon;
          
          return (
            <Card 
              key={tier.id} 
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${
                tier.popular ? 'border-2 border-violet-300 shadow-md' : ''
              } ${isCurrentPlan ? 'ring-2 ring-amber-400' : ''}`}
            >
              {tier.popular && (
                <div className="absolute top-0 right-0">
                  <div className="bg-violet-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                </div>
              )}
              
              {isCurrentPlan && (
                <div className="absolute top-0 left-0">
                  <div className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-br-lg flex items-center gap-1">
                    <Star className="w-3 h-3" /> Current Plan
                  </div>
                </div>
              )}

              <CardHeader className={`${tier.bgColor} pb-4`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${tier.color}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{tier.name}</CardTitle>
                    <p className="text-xs text-slate-500">{tier.subtitle}</p>
                  </div>
                </div>
                <CardDescription className="text-sm">{tier.description}</CardDescription>
              </CardHeader>

              <CardContent className="pt-6">
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-slate-900">
                      {billingCycle === 'monthly' ? tier.monthlyRange : tier.yearlyRange}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {billingCycle === 'monthly' ? 'per month' : 'per year'}
                  </p>
                </div>

                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${feature.highlight ? 'text-violet-500' : 'text-green-500'}`} />
                      ) : (
                        <div className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border-2 border-slate-200" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'} ${feature.highlight ? 'font-medium' : ''}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-4">
                <Button 
                  className={`w-full ${
                    isCurrentPlan 
                      ? 'bg-slate-100 text-slate-500 hover:bg-slate-100 cursor-default' 
                      : tier.enterprise 
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                        : tier.popular 
                          ? 'bg-equipr-black hover:bg-equipr-black/90 text-white'
                          : ''
                  }`}
                  disabled={isCurrentPlan}
                >
                  {isCurrentPlan ? 'Current Plan' : tier.cta}
                  {!isCurrentPlan && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Add-ons Section */}
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-violet-500" />
          <h2 className="text-xl font-semibold text-slate-900">Optional Add-Ons</h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {addOns.map((addon, idx) => (
            <Card key={idx} className="border-dashed border-2 hover:border-violet-300 transition-colors">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{addon.name}</CardTitle>
                  <Badge variant="outline" className="text-violet-600 border-violet-200">
                    {addon.type === 'one-time' ? 'One-time' : 'Per month'}
                  </Badge>
                </div>
                <CardDescription>{addon.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-2xl font-bold text-slate-900">{addon.price}</span>
                  <span className="text-sm text-slate-500">
                    {addon.type === 'one-time' ? 'one-time fee' : 'per additional EBA'}
                  </span>
                </div>
                <ul className="space-y-2">
                  {addon.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full">
                  Learn More
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      {/* Feature Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Feature Comparison
          </CardTitle>
          <CardDescription>
            Detailed comparison of what&apos;s included in each tier
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Feature</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-blue-600">Assist</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-violet-600">Growth</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-amber-600">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">EBA Documents</td>
                  <td className="py-3 px-4 text-center text-sm">1-2</td>
                  <td className="py-3 px-4 text-center text-sm">Up to 5</td>
                  <td className="py-3 px-4 text-center text-sm font-medium text-amber-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">Timesheet Import</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">AI EBA Interpretation</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">Excel/CSV Export</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">Direct Xero Export</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">EBA Versioning</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">Admin Rule Editor</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm text-slate-700">Priority Support</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center text-slate-300">—</td>
                  <td className="py-3 px-4 text-center"><Check className="w-4 h-4 text-green-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Contact Section */}
      <div className="mt-8 text-center">
        <p className="text-slate-600 mb-4">
          Need a custom solution or have questions about our pricing?
        </p>
        <Button variant="outline" size="lg">
          <Building2 className="w-4 h-4 mr-2" />
          Contact Sales
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionPage;
